/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { PaperPlaneRightIcon, UserCircleIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";

const Relay = () => {
  const { sessionId } = useParams();
  const [username, setUsername] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState([]);
  const [savedUsername, setSavedUsername] = useState(localStorage.getItem(`golucru_relay_user_${sessionId}`) || "");

  const sendMessage = async (e) => {
    e?.preventDefault();
    const u = (username || savedUsername || "anon").trim();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post("/chat/relay", { session_id: sessionId, username: u, text: t });
      localStorage.setItem(`golucru_relay_user_${sessionId}`, u);
      setSavedUsername(u);
      setSent((p) => [{ id: Date.now(), username: u, text: t }, ...p].slice(0, 25));
      setText("");
      toast.success("Message sent to agent", { duration: 1000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex flex-col">
      <header className="glass border-b border-[#222233] px-4 py-3 flex items-center gap-2 shrink-0">
        <Logo size={32} withBackground />
        <div>
          <div className="font-display font-black text-sm">LUCRÜ Relay</div>
          <div className="text-[10px] text-white/40 font-mono">session · {sessionId?.slice(0, 8)}</div>
        </div>
      </header>

      <main className="flex-1 flex flex-col max-w-md w-full mx-auto px-4 py-6 gap-4">
        <div className="glass rounded-2xl p-5">
          <div className="text-xs text-white/50 mb-2">
            Type any chat message to be read aloud by the streamer's AI co-host.
          </div>
          <form onSubmit={sendMessage} className="space-y-3">
            <div className="relative">
              <UserCircleIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                value={username || savedUsername}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Chatter name (e.g. big_fan_42)"
                data-testid="relay-username-input"
                className="w-full bg-[#05050A] border border-[#222233] rounded-xl pl-9 pr-3 py-3 text-sm outline-none focus:border-[#00E5FF]"
              />
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Type the chat message…"
              rows={3}
              data-testid="relay-text-input"
              className="w-full bg-[#05050A] border border-[#222233] rounded-xl p-3 text-sm outline-none focus:border-[#00E5FF] resize-none"
            />
            <button
              type="submit"
              disabled={sending || !text.trim()}
              data-testid="relay-send-button"
              className="btn-pill btn-primary w-full justify-center disabled:opacity-40"
            >
              <PaperPlaneRightIcon size={16} weight="fill" /> Send to agent
            </button>
          </form>
        </div>

        <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-2">
          <span>Recently sent</span>
          <span className="flex-1 h-px bg-[#222233]" />
        </div>
        <div className="space-y-2" data-testid="relay-sent-list">
          {sent.length === 0 && (
            <div className="text-xs text-white/30 text-center py-4">No messages sent yet.</div>
          )}
          {sent.map((m) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-xl px-3 py-2 text-xs"
            >
              <span className="text-[#00E5FF] font-semibold">{m.username}</span>{" "}
              <span className="text-white/80">{m.text}</span>
            </motion.div>
          ))}
        </div>
      </main>

      <footer className="border-t border-[#222233] px-4 py-3 text-center text-[10px] text-white/40">
        Keep this tab open · messages are read instantly
      </footer>
    </div>
  );
};

export default Relay;
